import { Box, Button, Card, Form, Section } from '@factorialco/deprecated-design-system'
import { Form as FactorialForm } from 'factorial-form'

import { observer } from 'mobx-react'

import TextField from 'modules/core/components/TextField'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import i18n from 'modules/core/lib/i18n'

import type GDPRDataRetention from 'modules/ats/store/models/GDPRDataRetention'

class LimitsForm extends FactorialForm {
  constructor(values: {
    consent_limit_enabled: boolean
    default_retention_limit: number
    talent_pool_retention_limit: number
  }) {
    super(values, {
      consent_limit_enabled: 'boolean',
      default_retention_limit: 'number',
      talent_pool_retention_limit: 'number',
    })
  }
}

interface Props {
  currentRetention: GDPRDataRetention
}

const DataRetention = ({ currentRetention }: Props) => {
  const form = useLazyRef(() => new LimitsForm(currentRetention.toJS())).current

  const { performAction: saveDataForm } = useAsyncAction(async () => {
    await currentRetention?.save(form.data())
  })

  const { performAction: saveToggleData } = useAsyncAction(async () => {
    const consentLimitEnabled = form.get('consent_limit_enabled').value

    await currentRetention?.save({
      consent_limit_enabled: consentLimitEnabled,
      ...(consentLimitEnabled === false && {
        auto_deletion_enabled: false,
        consent_renewal_email_enabled: false,
      }),
    })
  })

  const active = currentRetention?.attributes.consent_limit_enabled

  const toggle = (value: boolean) => {
    form.setValues({
      consent_limit_enabled: value,
    })

    saveToggleData()
  }

  return (
    <Section.TwoThirds
      description={i18n.t('app_store_v2.apps.ats.settings.data_retention.description')}
      illustration="visibility"
      title={i18n.t('app_store_v2.apps.ats.settings.data_retention.title')}
      link={{
        label: i18n.t('app_store_v2.apps.ats.settings.data_retention.link'),
        href: i18n.t('app_store_v2.apps.ats.settings.data_retention.link_url'),
      }}
    >
      <Box>
        <Card>
          <Card.Title size="small">
            {i18n.t('ats.settings.data_retention.permission_expiration.title')}
          </Card.Title>
          <Card.Description>
            {i18n.t('ats.settings.data_retention.permission_expiration.description')}
          </Card.Description>
          <Card.ExpandableContent expanded={active} onExpandChange={toggle}>
            <Form onSubmit={saveDataForm} padding={null}>
              <TextField
                label={i18n.t(
                  'ats.settings.data_retention.permission_expiration.inputs_label.job_applications'
                )}
                value={form.get('default_retention_limit').value}
                onChange={(value) => form.setValues({ default_retention_limit: Number(value) })}
                indicator={i18n.t('ats.settings.data_retention.permission_expiration.months')}
              />
              <TextField
                label={i18n.t(
                  'ats.settings.data_retention.permission_expiration.inputs_label.talent_pool'
                )}
                value={form.get('talent_pool_retention_limit').value}
                onChange={(value) => form.setValues({ talent_pool_retention_limit: Number(value) })}
                indicator={i18n.t('ats.settings.data_retention.permission_expiration.months')}
              />
              {form.isDirty && (
                <Form.Buttons>
                  <Button submit>{i18n.t('common.submit')}</Button>
                </Form.Buttons>
              )}
            </Form>
          </Card.ExpandableContent>
        </Card>
      </Box>
    </Section.TwoThirds>
  )
}

export default observer(DataRetention)
